import { forwardRef, lazy, useEffect } from 'react';
import type {
  FloorPlanRealisticHandle,
  FloorPlanRealisticViewProps,
} from './FloorPlanRealisticView';

/**
 * Stands in for the realistic view when its code could not be fetched (a
 * dropped connection, a deploy that replaced the chunk): it asks for the
 * diagram rather than letting the failed import reach — and blank — the app.
 */
const RealisticViewUnavailable = forwardRef<FloorPlanRealisticHandle, FloorPlanRealisticViewProps>(
  function RealisticViewUnavailable({ onUnsupported }, _ref) {
    useEffect(() => {
      onUnsupported?.();
    }, [onUnsupported]);
    return null;
  },
);

/** The realistic view's module, or the stand-in when it cannot be loaded. */
export function loadRealisticView(
  importer: () => Promise<{ default: typeof RealisticViewUnavailable }> = () =>
    import('./FloorPlanRealisticView'),
): Promise<{ default: typeof RealisticViewUnavailable }> {
  return importer().catch(() => ({ default: RealisticViewUnavailable }));
}

/**
 * The realistic (WebGL) view, loaded on first use. three.js is a few hundred
 * kilobytes: fetched the first time somebody opens a realistic view — the 3D
 * modal or the AI Render Studio — never with the floor plan itself.
 */
export const LazyRealisticView = lazy(() => loadRealisticView());

/** Whether this browser can draw WebGL at all. */
export function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}
